import FileUploadUploadStatus from './UploadStatus';
import FileUploadInfoDefault from './InfoDefault';
import FileUploadInfoWorking from './InfoWorking';
import FileUploadInfoError from './InfoError';

import styles from './FileUpload.module.scss';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';
import { Box, Button, ButtonGroup, Stack } from '@chaser/components';
import PQueue from 'p-queue';

const CONCURRENT_UPLOADS_LIMIT = 6;

/**
 * Called before upload starts.
 *
 * @callback onBeforeUploadCallback
 * @param {string[]} filesToUpload List of file names that were accepted by Dropzone.
 *
 * @returns {void}
 */

/**
 * Called when a file upload fails.
 *
 * @callback onFileUploadErrorCallback
 * @param {string} name File name
 * @param {string} prefix Upload group identifier obtained from `getUploadInfo` (it can be empty)
 * @param {string} error Error message
 *
 * @returns {void}
 */

/**
 * Called when a file upload succeeds.
 *
 * @callback onFileUploadSuccessCallback
 * @param {string} name File name
 * @param {string} prefix Upload group identifier obtained from `getUploadInfo` (it can be empty)
 *
 * @returns {void}
 */

/**
 * Called when upload was attempted for all files. Needs to be async.
 *
 * If set, the component will display the "processing" state until Promise is resolved or rejected.
 * Gets arrays of successful and failed files. Please note that FileUpload component itself will
 * only show failed uploads when `showUploadStatus=true`, and even then only while upload is ongoing.
 * Once the upload is done, the status is hidden, so parent component should also also show upload
 * errors to the user if needed, e.g. by passing Error instance to `Promise.reject`.
 *
 * ```javascript
 * onAllFilesUploaded = {(successes, failures) => new Promise((resolve, reject) => {
 *   if (failures.length > 0) {
 *     const err = new Error('Failed to upload some files, cannot proceed.');
 *     err.rejectedFiles = failures;
 *     err.expose = true;
 *     reject(err);
 *   } else {
 *     setTimeout(() => {
 *       resolve();
 *     }, 3000);
 *   }
 * })}
 * ```
 *
 * @callback onAllFilesUploadedCallback
 * @param {Object[]} successes - An array of files that were successfully uploaded
 * @param {string} successes[].name - The name of the file
 * @param {string} successes[].prefix - The upload group identifier
 * @param {Object[]} failures - An array of files that failed to upload
 * @param {string} failures[].name - The name of the file
 * @param {string} failures[].prefix - The upload group identifier
 * @param {string|Error[]} failures[].errors - The error message or an array of Error objects
 *
 * @returns {Promise<void>}
 */

/**
 * A customizable file upload component.
 *
 * @param {Object} props
 * @param {function} props.getUploadInfo - An async function that returns the upload
 *     information for the files.
 * @param {Object} [props.accept] - The file types that the component will accept. The key
 *     is the file type and the value is an array of extensions. See
 *     https://react-dropzone.js.org/#src `accept` property.
 * @param {number} [props.maxSize] - The maximum size of the files that the component will
 *     accept (in bytes). See https://react-dropzone.js.org/#src `maxSize` property.
 * @param {boolean} [props.multiple] - Whether the component allows multiple file uploads.
 * @param {boolean} [props.disabled] - Whether the component is disabled.
 * @param {function} [props.validator] - A function that validates the files before upload.
 *     See https://react-dropzone.js.org/#src `validator` property.
 * @param {onBeforeUploadCallback} [props.onBeforeUpload] - A callback function that is called before
 *     upload starts.
 * @param {onFileUploadErrorCallback} [props.onFileUploadError] - A callback function that is called when a
 *     file upload fails.
 * @param {onFileUploadSuccessCallback} [props.onFileUploadSuccess] - A callback function that is called when
 *     a file upload succeeds.
 * @param {onAllFilesUploadedCallback} [props.onAllFilesUploaded] - An async callback function that is
 *     called when all files upload have been attempted.
 * @param {boolean} [props.showUploadStatus] - Whether to show the upload status.
 *
 * @return {JSX.Element} The file upload component.
 */
const FileUpload = ({
  accept,
  maxSize,
  getUploadInfo,
  multiple,
  disabled,
  validator,
  onBeforeUpload: onBeforeUploadProp,
  onFileUploadError: onFileUploadErrorProp,
  onFileUploadSuccess: onFileUploadSuccessProp,
  onAllFilesUploaded: onAllFilesUploadedProp,
  showUploadStatus,
  signal,
}) => {
  const [processing, setProcessing] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [filesToUpload, setFilesToUpload] = React.useState([]);
  const [filesReady, setFilesReady] = React.useState(false);
  const [uploadStatus, setUploadStatus] = React.useState({});

  const abortControllerRef = React.useRef(null);
  const parentSignalRef = React.useRef(signal);
  const onFileUploadErrorRef = React.useRef(onFileUploadErrorProp);
  const onFileUploadSuccessRef = React.useRef(onFileUploadSuccessProp);
  const onAllFilesUploadedRef = React.useRef(onAllFilesUploadedProp);
  const onBeforeUploadRef = React.useRef(onBeforeUploadProp);

  const isDisabled = useMemo(() => !!disabled || processing || uploading, [
    disabled,
    processing,
    uploading,
  ]);

  const onAllFilesUploaded = useCallback(async (successes, failures) => {
    if (onAllFilesUploadedRef.current) {
      setUploading(false);
      setProcessing(true);
      setError(null);

      try {
        await onAllFilesUploadedRef.current([...successes], [...failures]);
      } catch (err) {
        setError(err);
      } finally {
        setProcessing(false);
      }
    }
  }, []);

  const onFileUploadError = useCallback((name, prefix, error) => {
    if (onFileUploadErrorRef.current) {
      onFileUploadErrorRef.current(name, prefix, error);
    }
  }, []);

  const onFileUploadSuccess = useCallback((name, prefix) => {
    if (onFileUploadSuccessRef.current) {
      onFileUploadSuccessRef.current(name, prefix);
    }
  }, []);

  const onBeforeUpload = useCallback(filesToUpload => {
    if (onBeforeUploadRef.current) {
      onBeforeUploadRef.current([...filesToUpload]);
    }
  }, []);

  const onDrop = useCallback(() => {
    setError(null);
  }, []);

  const onDropAccepted = useCallback(
    acceptedFiles => {
      if (multiple) {
        setFilesToUpload(prev =>
          // update list, but skip duplicates; we only care about the name
          // since that's what will be used to identify files in further steps
          [...prev, ...acceptedFiles].filter(
            (file, index, arr) => arr.findIndex(f => f.name === file.name) === index,
          ),
        );
      } else {
        setFilesToUpload([...acceptedFiles]);
        setFilesReady(true);
      }
    },
    [multiple],
  );

  const onDropRejected = useCallback(rejectedFiles => {
    const err = new Error('Some files were rejected.');
    err.rejectedFiles = rejectedFiles;
    err.expose = true;
    setError(err);
  }, []);

  const onContinue = useCallback(ev => {
    ev.preventDefault();
    ev.stopPropagation();
    setFilesReady(true);
  }, []);

  const setFileUploadStatus = useCallback(
    (name, status, error, prefix) => {
      setUploadStatus(prev => ({
        ...prev,
        [name]: {
          ...prev[name],
          status,
          error,
        },
      }));

      if (status === 'success') {
        onFileUploadSuccess({
          name,
          prefix,
        });
      } else if (status === 'error') {
        onFileUploadError({
          name,
          prefix,
          error,
        });
      }
    },
    [onFileUploadError, onFileUploadSuccess],
  );

  const handleUpload = useCallback(
    async files => {
      setUploading(true);
      setError(null);

      try {
        const { invalidFileNames, urls, prefix, headers } = await getUploadInfo(
          files.map(file => file.name),
        );

        abortControllerRef.current.signal.throwIfAborted();

        if (invalidFileNames?.length > 0) {
          const err = new Error('Invalid file name');
          err.rejectedFiles = invalidFileNames.map(name => ({ name }));
          err.expose = true;
          setError(err);
          // no break, there may be some valid files that need to be uploaded
        }

        const filesWithLinks = Object.entries(urls || {})
          .map(([name, url]) => ({
            name,
            url,
            file: files.find(file => file.name === name),
          }))
          .filter(({ file }) => !!file);

        if (filesWithLinks.length === 0) {
          const err = new Error('Mismatched file names and URLs.');
          err.expose = true;
          throw err;
        }

        const successes = [];
        const failures = [];
        const queue = new PQueue({ concurrency: CONCURRENT_UPLOADS_LIMIT });

        filesWithLinks.forEach(({ name, url, file }) =>
          queue.add(
            async () =>
              fetch(url, {
                method: 'PUT',
                headers: headers,
                body: file,
                signal: abortControllerRef.current.signal,
              })
                .then(response => {
                  if (!response.ok) {
                    setFileUploadStatus(name, 'error', response.statusText, prefix);
                    failures.push({ name, prefix, errors: response.statusText });
                    return;
                  }

                  setFileUploadStatus(name, 'success', null, prefix);
                  successes.push({ name, prefix });
                })
                .catch(err => {
                  setFileUploadStatus(name, 'error', err.message, prefix);
                  failures.push({ name, prefix, errors: err.message });
                }),
            {
              signal: abortControllerRef.current.signal,
            },
          ),
        );

        await queue.onIdle();

        abortControllerRef.current.signal.throwIfAborted();

        onAllFilesUploaded(successes, failures);
      } catch (err) {
        setError(err);
      } finally {
        setUploading(false);
        setFilesReady(false);
        setFilesToUpload([]);
      }
    },
    [getUploadInfo, onAllFilesUploaded, setFileUploadStatus],
  );

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    accept,
    multiple,
    maxSize,
    disabled: isDisabled,
    noKeyboard: true,
    onDrop,
    onDropAccepted,
    onDropRejected,
    validator,
  });

  useEffect(() => {
    setUploadStatus(
      Object.fromEntries(
        filesToUpload.map(file => [
          file.name,
          {
            name: file.name,
            size: file.size,
            status: 'waiting',
            error: null,
          },
        ]),
      ),
    );
  }, [filesToUpload]);

  useEffect(() => {
    if (filesReady && filesToUpload.length) {
      onBeforeUpload(filesToUpload);
      void handleUpload(filesToUpload);
    }
  }, [filesReady, filesToUpload, handleUpload, onBeforeUpload]);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const parentSignal = parentSignalRef.current;
    const onAbort = () => {
      controller.abort();
    };

    if (parentSignal) {
      parentSignal.addEventListener('abort', onAbort);
    }

    return () => {
      controller.abort();

      if (parentSignal) {
        parentSignal.removeEventListener('abort', onAbort);
      }
    };
  }, []);

  return (
    <Stack gap="medium">
      <Box
        {...getRootProps({
          className: cx(styles.dropzone, {
            [styles.isDragAccept]: isDragAccept,
            [styles.isDragReject]: isDragReject,
          }),
        })}
        p="medium"
      >
        <input {...getInputProps()} />
        <Stack gap="medium" alignItems="center">
          {!error && !uploading && !processing && (
            <FileUploadInfoDefault multiple={multiple} maxSize={maxSize} accept={accept} />
          )}
          {(uploading || processing) && (
            <FileUploadInfoWorking job={uploading ? 'uploading' : 'processing'} />
          )}
          {error && <FileUploadInfoError error={error} />}
          <ButtonGroup>
            <Button
              type="button"
              size="narrow"
              appearance="secondary"
              className={styles.button}
              disabled={isDisabled}
            >
              Select {multiple ? 'files' : 'a file'}
            </Button>
            {multiple && (
              <Button
                type="button"
                size="narrow"
                appearance="primary"
                className={styles.button}
                disabled={filesToUpload.length === 0 || isDisabled}
                onClick={onContinue}
              >
                Continue
              </Button>
            )}
          </ButtonGroup>
        </Stack>
      </Box>
      {showUploadStatus && filesToUpload.length > 0 && (
        <FileUploadUploadStatus uploadStatus={uploadStatus} />
      )}
    </Stack>
  );
};

export default FileUpload;
